import React from 'react';
import styled from 'styled-components';
import { Gear } from '../../assets/icons/svgs';
import { Body1, Container } from '../../libs/nvstr-common-ui.es';
import { useDispatch } from 'react-redux';
import { advancedSettingsActionTypes } from '../../constants/actionTypes';
import {
  ADVANCED_SETTING_VALUE_TYPES,
  AdvancedSettingsFields,
  getAdvancedSettingDisplayName,
  getAdvancedSettingType,
  useAdvancedSettings,
  useAdvancedSettingsFields,
  useShowAdvancedSettings,
} from '../../hooks/application/useAdvancedSettings';
import { Modal } from '../../components/layout/Modal';
import { CloseButton } from '../../components/buttons/CloseButton';
import { gptModels } from '../../constants';
import { logger } from '../../utils/usefulFuncs';

const AdvancedSettingsButtonWrapper = styled.div`
  position: absolute;
  top: ${({ Top }) => Top + 16 + 'px'};
  right: 16px;
  z-index: 100;
  cursor: pointer;

  * {
    cursor: pointer;
  }
`;
const AdvancedSettingsWrapper = styled.div`
  padding: 0;

  min-width: 340px;
`;
const CloseButtonWrapper = styled.div`
  cursor: pointer;

  z-index: 1116;

  * {
    z-index: 1116;
  }

  svg {
    height: 12px;
    width: 12px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const GearIconWrapper = styled.div`
  svg {
    height: 18px;
    width: 18px;

    fill: ${({ theme }) => theme.themeColors.text};
  }
`;
const Wrapper = styled.div`
  padding: 24px 0 24px 0;
`;
const FormWrapper = styled.div`
  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 8px;
  }

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 8px;
    -webkit-appearance: auto !important;
  }

  textarea {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    min-height: 100px;
    width: 100%;
    padding: 16px;
  }
`;
const Label = styled.div`
  padding-bottom: 8px;
`;
const CheckboxInput = styled.input`
  cursor: pointer;
  margin-top: 0;
  margin-left: 0px;
  width: 14px !important;
`;
const CheckboxLabel = styled.label`
  cursor: pointer;
  margin: 0;
  padding-left: 8px;
  user-select: none;
`;

function sortModels(list) {
  return list.sort();
}

function sortItemsByValueType(items) {
  const order = [
    ADVANCED_SETTING_VALUE_TYPES.string,
    ADVANCED_SETTING_VALUE_TYPES.integer,
    ADVANCED_SETTING_VALUE_TYPES.float,
    ADVANCED_SETTING_VALUE_TYPES.bool,
  ];

  return items.sort((a, b) => {
    const aValueType = getAdvancedSettingType(a);
    const bValueType = getAdvancedSettingType(b);
    return order.indexOf(aValueType) - order.indexOf(bValueType);
  });
}

const ModelField = ({ field }) => {
  const gptModelItems = sortModels(Object.values(gptModels));
  const state = useAdvancedSettings();
  const dispatch = useDispatch();

  const setValue = (k, v) => {
    dispatch({
      type: advancedSettingsActionTypes.setAdvancedSettings,
      payload: {
        [k]: v,
      },
    });
  };

  const getValue = (k) => {
    return state[k];
  };

  const fieldName = field;
  const value = getValue(fieldName);
  const onChange = (e) => setValue(fieldName, e.target.value);
  return (
    <Container top={16}>
      <Label>Model (defaults to recommended version)</Label>
      <select onChange={onChange} value={value}>
        {gptModelItems.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
    </Container>
  );
};

const MaxAnswerLengthField = ({ field }) => {
  const state = useAdvancedSettings();
  const dispatch = useDispatch();

  const setValue = (k, v) => {
    dispatch({
      type: advancedSettingsActionTypes.setAdvancedSettings,
      payload: {
        [k]: v,
      },
    });
  };

  const getValue = (k) => {
    return state[k];
  };

  const fieldName = field;
  const value = getValue(fieldName);

  const onMaxAnswerLengthChangeEffect = (e) => {
    const maxValue = 4096;
    const value = e.target.value;
    const intValue = parseInt(value, 10);
    if (intValue > maxValue) {
      setValue('maxAnswerLength', maxValue.toString());
    } else {
      setValue('maxAnswerLength', value);
    }
  };

  return (
    <Container top={16}>
      <Label>Max Answer Length</Label>
      <input id="maxAnswerLength" value={value} onChange={onMaxAnswerLengthChangeEffect} />
    </Container>
  );
};

const CUSTOM_FIELDS = {
  [AdvancedSettingsFields.model]: (props) => <ModelField {...props} />,
  [AdvancedSettingsFields.maxAnswerLength]: (props) => <MaxAnswerLengthField {...props} />,
};

const Field = (props) => {
  const { field } = props;

  const state = useAdvancedSettings();
  const dispatch = useDispatch();

  const setValue = (k, v) => {
    dispatch({
      type: advancedSettingsActionTypes.setAdvancedSettings,
      payload: {
        [k]: v,
      },
    });
  };

  const getValue = (k) => {
    return state[k];
  };

  const fieldName = field;

  const customFieldFunc = CUSTOM_FIELDS[fieldName];
  const isCustomField = !!customFieldFunc;
  if (isCustomField) {
    return customFieldFunc(props);
  }

  const fieldLabel = getAdvancedSettingDisplayName(fieldName);
  const fieldType = getAdvancedSettingType(fieldName);

  if (fieldType === ADVANCED_SETTING_VALUE_TYPES.bool) {
    const value = getValue(fieldName);
    const onCheckboxChange = () => {
      setValue(fieldName, !value);
    };
    return (
      <Container top={36} row verticallyCenter>
        <CheckboxInput type="checkbox" id={'checkbox-' + fieldLabel} checked={value} onClick={onCheckboxChange} />
        <CheckboxLabel htmlFor={'checkbox-' + fieldLabel} className="not-selectable">
          <Container bottom={3}>{fieldLabel}</Container>
        </CheckboxLabel>
      </Container>
    );
  }

  if (fieldType === ADVANCED_SETTING_VALUE_TYPES.string) {
    const value = getValue(fieldName);

    const onChange = (e) => {
      const v = e.target.value;
      setValue(fieldName, v);
    };
    return (
      <Container top={24}>
        <Label>{fieldLabel}</Label>
        <input id={fieldName} name={fieldName} value={value} onChange={onChange} />
      </Container>
    );
  }

  if (fieldType === ADVANCED_SETTING_VALUE_TYPES.integer) {
    const value = getValue(fieldName);
    const onChange = (e) => {
      const v = e.target.value;
      setValue(fieldName, v);
    };
    return (
      <Container top={24}>
        <Label>{fieldLabel}</Label>
        <input id={fieldName} name={fieldName} value={value} onChange={onChange} />
      </Container>
    );
  }

  if (fieldType === ADVANCED_SETTING_VALUE_TYPES.float) {
    const value = getValue(fieldName);
    const onChange = (e) => {
      const v = e.target.value;
      setValue(fieldName, v);
    };
    return (
      <Container top={24}>
        <Label>{fieldLabel}</Label>
        <input id={fieldName} name={fieldName} value={value} onChange={onChange} />
      </Container>
    );
  }

  logger('missing field renderer', field);
  return null;
};

export function AdvancedSettingsButton({ fields, top }) {
  const dispatch = useDispatch();

  const onOpenClick = () => {
    dispatch({ type: advancedSettingsActionTypes.showAdvancedSettings, payload: fields });
  };
  return (
    <AdvancedSettingsButtonWrapper Top={top || 0}>
      <GearIconWrapper onClick={onOpenClick}>
        <Gear />
      </GearIconWrapper>
    </AdvancedSettingsButtonWrapper>
  );
}

export function AdvancedSettingsModal() {
  const dispatch = useDispatch();
  // const enableBetaFeatures = useHasBetaFeaturesEnabled();

  const isShowing = useShowAdvancedSettings();
  const fields = useAdvancedSettingsFields();

  const [sortedFields, setSortedFields] = React.useState([]);

  React.useEffect(() => {
    if (fields) {
      setSortedFields(sortItemsByValueType(fields));
    }
  }, [fields]);

  const onCloseClick = () => dispatch({ type: advancedSettingsActionTypes.hideAdvancedSettings });

  if (!isShowing) return null;

  return (
    <Modal onDismiss={onCloseClick}>
      <AdvancedSettingsWrapper>
        <Container row verticallyCenter spaceBetween>
          <Container>
            <Body1 bold>Advanced Settings</Body1>
          </Container>
          <CloseButtonWrapper>
            <CloseButton onClick={onCloseClick} />
          </CloseButtonWrapper>
        </Container>
        <Wrapper>
          <FormWrapper>
            <Container>
              {sortedFields.map((field) => (
                <Field field={field} />
              ))}
            </Container>
          </FormWrapper>
        </Wrapper>
      </AdvancedSettingsWrapper>
    </Modal>
  );
}
